import { useSyncExternalStore } from "react";
import { PRODUCT_API_PREFIX, PRODUCT_KEYS, type ProductKey } from "../../../shared/products/keys";

/**
 * A product was disabled while the user was in it (spec §6 "revocation mid-session"): any
 * /api/v1/<product>/* call answers 404 PRODUCT_DISABLED. One pending revocation at a time, so a
 * screen with six queries in flight produces one toast and one navigation, not six.
 *
 * A UI signal only: the server already refused the call. Hire has no API prefix, so a hire 404
 * never maps to a product and never reaches this store.
 */
export function productOfApiUrl(url: string): ProductKey | null {
  const path = url.split("?")[0];
  for (const key of PRODUCT_KEYS) {
    const prefix = PRODUCT_API_PREFIX[key];
    if (prefix && (path === prefix || path.startsWith(`${prefix}/`))) return key;
  }
  return null;
}

let pending: ProductKey | null = null;
/**
 * What was consumed recently. The handler consumes inside its effect, but the screen it is
 * unmounting still has queries in flight; each of their 404s would otherwise re-arm `pending`
 * and produce a second toast and navigation.
 */
const recentlyConsumed = new Map<ProductKey, number>();
const RECENT_MS = 10_000;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

export function reportProductDisabled(productKey: ProductKey): void {
  const at = recentlyConsumed.get(productKey);
  if (pending || (at !== undefined && Date.now() - at < RECENT_MS)) return;
  pending = productKey;
  notify();
}

export function consumeProductRevocation(): void {
  if (pending) recentlyConsumed.set(pending, Date.now());
  pending = null;
  notify();
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const read = () => pending;

/** The revocation pending right now, read outside React (the handler re-checks it in its effect). */
export function getProductRevocation(): ProductKey | null {
  return pending;
}

export function useProductRevocation(): ProductKey | null {
  return useSyncExternalStore(subscribe, read, read);
}

export function resetProductRevocationForTests(): void {
  pending = null;
  recentlyConsumed.clear();
  notify();
}
